/** Sticky bottom-bar для bulk-операций на products list page. */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Percent, Trash2, X, Power } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";

interface Props {
  selectedIds: number[];
  onClear: () => void;
}

type ModalKind = null | "price" | "delete";

export function BulkActionsBar({ selectedIds, onClear }: Props): JSX.Element | null {
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<ModalKind>(null);
  const [pricePercent, setPricePercent] = useState<string>("");
  const [priceTarget, setPriceTarget] = useState<"base_price" | "sale_price">(
    "base_price",
  );

  // ─── Mutations ──────────────────────────────────────────────────────
  const toggleVisibility = useMutation({
    mutationFn: async (active: boolean) => {
      await api.post("/products/bulk-update", {
        ids: selectedIds,
        patch: { is_active: active },
      });
    },
    onSuccess: (_, active) => {
      toast.success(
        active
          ? `${selectedIds.length} товаров активированы`
          : `${selectedIds.length} товаров скрыты`,
      );
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      onClear();
    },
    onError: (e: any) => toast.error(e?.message ?? "Ошибка"),
  });

  const applyPercent = useMutation({
    mutationFn: async () => {
      const percent = Number(pricePercent);
      if (Number.isNaN(percent)) throw new Error("Введите число");
      const { data } = await api.post("/products/bulk-price-percent", {
        ids: selectedIds,
        percent,
        target: priceTarget,
      });
      return data as { affected_count: number };
    },
    onSuccess: (d) => {
      toast.success(`Цена изменена у ${d.affected_count} товаров`);
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      setModal(null);
      setPricePercent("");
      onClear();
    },
    onError: (e: any) => toast.error(e?.message ?? "Ошибка"),
  });

  const bulkDelete = useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/products/bulk-delete", {
        ids: selectedIds,
      });
      return data as { affected_count: number };
    },
    onSuccess: (d) => {
      toast.success(`Удалено: ${d.affected_count}`);
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      setModal(null);
      onClear();
    },
    onError: (e: any) => toast.error(e?.message ?? "Ошибка"),
  });

  if (selectedIds.length === 0) return null;

  return (
    <>
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-xl border bg-card shadow-lg px-5 py-3">
        <span className="text-sm font-medium">
          Выбрано: <strong>{selectedIds.length}</strong>
        </span>

        <div className="h-6 w-px bg-border" />

        <Button
          size="sm"
          variant="outline"
          onClick={() => toggleVisibility.mutate(true)}
          disabled={toggleVisibility.isPending}
        >
          <Power className="w-4 h-4" />
          Активировать
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => toggleVisibility.mutate(false)}
          disabled={toggleVisibility.isPending}
        >
          <Power className="w-4 h-4" />
          Скрыть
        </Button>
        <Button size="sm" variant="outline" onClick={() => setModal("price")}>
          <Percent className="w-4 h-4" />
          Изменить цену
        </Button>
        <Button
          size="sm"
          variant="destructive"
          onClick={() => setModal("delete")}
        >
          <Trash2 className="w-4 h-4" />
          Удалить
        </Button>

        <div className="h-6 w-px bg-border" />

        <Button size="sm" variant="ghost" onClick={onClear} title="Очистить">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Price change modal */}
      <Dialog open={modal === "price"} onOpenChange={(v) => !v && setModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Изменить цену</DialogTitle>
            <DialogDescription>
              Применить ±N% к выбранным {selectedIds.length} товарам.
              Минус — скидка, плюс — наценка.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={priceTarget === "base_price"}
                onChange={() => setPriceTarget("base_price")}
              />
              <span>Базовая цена</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={priceTarget === "sale_price"}
                onChange={() => setPriceTarget("sale_price")}
              />
              <span>Цена со скидкой (только где задана)</span>
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                placeholder="−15"
                value={pricePercent}
                onChange={(e) => setPricePercent(e.target.value)}
              />
              <span className="text-lg">%</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Пример: −15 → цены упадут на 15%. 25 → вырастут на 25%.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>
              Отмена
            </Button>
            <Button
              onClick={() => applyPercent.mutate()}
              disabled={applyPercent.isPending || !pricePercent}
            >
              {applyPercent.isPending && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              Применить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm modal */}
      <Dialog open={modal === "delete"} onOpenChange={(v) => !v && setModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Удалить выбранные товары?</DialogTitle>
            <DialogDescription>
              Будет выполнено soft-delete для {selectedIds.length} товаров.
              Они скроются из каталога, но останутся в БД (можно
              восстановить через support).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={() => bulkDelete.mutate()}
              disabled={bulkDelete.isPending}
            >
              {bulkDelete.isPending && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              Удалить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
